import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useBlocker, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactFlow, {
  addEdge, applyEdgeChanges, applyNodeChanges,
  Background, BackgroundVariant, ControlButton, Controls, MiniMap,
} from 'reactflow';
import type { Node, Edge, Connection, NodeChange, EdgeChange, ReactFlowInstance } from 'reactflow';
import {
  ArrowLeft, BookOpen, CalendarClock, Clock, Download, History, Lock, Network, Settings2, Webhook,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { autoLayout, type Positions } from '../lib/autoLayout';
import { EdgeActionsContext } from '../components/EdgeActionsContext';
import { DeletableEdge } from '../components/edges/DeletableEdge';
import 'reactflow/dist/style.css';
import { getFlow, updateFlow, publishFlow, exportFlow, listRevisions, restoreRevision } from '../api/flows';
import { getExecution, getExecutionSteps } from '../api/executions';
import { FlowDefinition, FlowNode as FlowNodeModel } from '../types';
import { ExecutionViewProvider } from '../components/ExecutionContext';
import { ExecutionPicker } from '../components/ExecutionPicker';
import { WebhookNode } from '../components/nodes/WebhookNode';
import { TransformNode } from '../components/nodes/TransformNode';
import { HttpNode } from '../components/nodes/HttpNode';
import { TokenLookupNode } from '../components/nodes/TokenLookupNode';
import { EvalNode } from '../components/nodes/EvalNode';
import { SwitchNode } from '../components/nodes/SwitchNode';
import { LookupNode } from '../components/nodes/LookupNode';
import { WaitNode } from '../components/nodes/WaitNode';
import { RetryNode } from '../components/nodes/RetryNode';
import { ForEachNode } from '../components/nodes/ForEachNode';
import { SubFlowNode } from '../components/nodes/SubFlowNode';
import { JoinNode } from '../components/nodes/JoinNode';
import { HttpPaginateNode } from '../components/nodes/HttpPaginateNode';
import { HttpTransferNode } from '../components/nodes/HttpTransferNode';
import { WatermarkReadNode } from '../components/nodes/WatermarkReadNode';
import { WatermarkWriteNode } from '../components/nodes/WatermarkWriteNode';
import { CryptoNode } from '../components/nodes/CryptoNode';
import { CodeNode } from '../components/nodes/CodeNode';
import { CommentNode } from '../components/nodes/CommentNode';
import { FailNode } from '../components/nodes/FailNode';
import { NodeConfigPanel } from '../components/NodeConfigPanel';
import { ScheduleModal } from '../components/ScheduleModal';
import { WebhookSecurityModal } from '../components/WebhookSecurityModal';
import { FlowSettingsModal } from '../components/FlowSettingsModal';
import { StepPicker } from '../components/StepPicker';
import { TemplateReferenceDrawer } from '../components/TemplateReferenceDrawer';

const nodeTypes = {
  webhook: WebhookNode,
  transform: TransformNode,
  http: HttpNode,
  'token-lookup': TokenLookupNode,
  eval: EvalNode,
  switch: SwitchNode,
  lookup: LookupNode,
  wait: WaitNode,
  retry: RetryNode,
  'foreach': ForEachNode,
  'subflow': SubFlowNode,
  'join': JoinNode,
  'http-paginate': HttpPaginateNode,
  'http-transfer': HttpTransferNode,
  'watermark-read': WatermarkReadNode,
  'watermark-write': WatermarkWriteNode,
  crypto: CryptoNode,
  code: CodeNode,
  comment: CommentNode,
  fail: FailNode,
};

const edgeTypes = {
  deletable: DeletableEdge,
};

function toRfNodes(nodes: FlowNodeModel[]): Node[] {
  return nodes.map((n, i) => ({
    id: n.id,
    type: n.type,
    data: { label: n.id, ...(n.config ?? {}) },
    position: n.position ?? { x: 200, y: i * 120 },
  }));
}

// Fallback size for nodes React Flow has not measured yet.
const DEFAULT_NODE_SIZE = { width: 160, height: 70 };

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

const NODE_STATUS_CLASS: Record<string, string> = {
  Queued: 'ff-node-status-queued',
  Running: 'ff-node-status-running',
  Completed: 'ff-node-status-completed',
  Failed: 'ff-node-status-failed',
};

function toRfEdges(edges: { source: string; target: string; sourceHandle?: string | null }[]): Edge[] {
  return edges.map(e => ({
    id: `${e.source}-${e.sourceHandle ?? 'default'}-${e.target}`,
    source: e.source,
    target: e.target,
    sourceHandle: e.sourceHandle ?? null,
    type: 'deletable',
  }));
}

export function FlowBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const executionId = searchParams.get('execution');
  const compareId = searchParams.get('compare');
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [panelInitialTab, setPanelInitialTab] = useState<'config' | 'input' | 'output' | 'try' | undefined>(undefined);
  const [autoOpened, setAutoOpened] = useState(false);
  const [rfNodes, setRfNodes] = useState<Node[]>([]);
  const [rfEdges, setRfEdges] = useState<Edge[]>([]);
  const [initialized, setInitialized] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [webhookSecurityOpen, setWebhookSecurityOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [referenceOpen, setReferenceOpen] = useState(false);
  const [referenceSection, setReferenceSection] = useState<string | undefined>(undefined);
  const [rfInstance, setRfInstance] = useState<ReactFlowInstance | null>(null);
  // Positions before the last auto-layout, for its undo button. The builder has no general undo.
  const [layoutUndo, setLayoutUndo] = useState<Positions | null>(null);

  const { data: execution } = useQuery({
    queryKey: ['executions', executionId],
    queryFn: () => getExecution(executionId!),
    enabled: !!executionId,
  });
  const { data: executionSteps = [] } = useQuery({
    queryKey: ['executions', executionId, 'steps'],
    queryFn: () => getExecutionSteps(executionId!),
    enabled: !!executionId,
  });
  const { data: compareExecution } = useQuery({
    queryKey: ['executions', compareId],
    queryFn: () => getExecution(compareId!),
    enabled: !!compareId,
  });
  const { data: compareSteps = [] } = useQuery({
    queryKey: ['executions', compareId, 'steps'],
    queryFn: () => getExecutionSteps(compareId!),
    enabled: !!compareId,
  });

  const openReference = useCallback((section?: string) => {
    setReferenceSection(section);
    setReferenceOpen(true);
  }, []);

  const { data: flow, isLoading } = useQuery({
    queryKey: ['flows', id],
    queryFn: () => getFlow(id!),
  });

  const definition: FlowDefinition = useMemo(() => {
    if (!flow) return { nodes: [], edges: [] };
    try { return JSON.parse(flow.definition); } catch { return { nodes: [], edges: [] }; }
  }, [flow]);

  // Sync from server on first load only
  useEffect(() => {
    if (flow && !initialized) {
      setRfNodes(toRfNodes(definition.nodes));
      setRfEdges(toRfEdges(definition.edges));
      setLayoutUndo(null);
      setInitialized(true);
    }
  }, [flow, initialized, definition]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const nodes: FlowNodeModel[] = rfNodes.map(n => ({
        id: n.id,
        type: n.type as FlowNodeModel['type'],
        config: n.data,
        position: n.position,
      }));
      const edges = rfEdges.map(e => ({ source: e.source, target: e.target, sourceHandle: e.sourceHandle ?? null }));
      // PUT replaces the whole flow — every field the builder does not edit must be
      // echoed back or the server clears it (tags, description, batch config).
      return updateFlow(id!, {
        name: flow!.name,
        definition: JSON.stringify({ nodes, edges }),
        batchConfig: flow!.batchConfig ?? undefined,
        labelTemplate: flow!.labelTemplate ?? null,
        tags: flow!.tags,
        description: flow!.description ?? null,
      });
    },
    onSuccess: () => {
      setIsDirty(false);
      qc.invalidateQueries({ queryKey: ['flows', id] });
      qc.invalidateQueries({ queryKey: ['flows', id, 'revisions'] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: (revisionName: string) => publishFlow(id!, { revisionName }),
    onSuccess: () => {
      setPublishOpen(false);
      qc.invalidateQueries({ queryKey: ['flows', id] });
      qc.invalidateQueries({ queryKey: ['flows', id, 'revisions'] });
    },
  });

  // Block in-app navigation (React Router) when there are unsaved changes.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const ok = window.confirm(
        'You have unsaved changes.\n\nOK = discard changes and leave the page\nCancel = stay on this page',
      );
      if (ok) {
        setIsDirty(false);
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  // Block browser-level navigation (refresh, close tab, URL change).
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Saving only writes the draft, so it needs no confirmation and no revision name — going live
  // is a separate, deliberate publish.
  function handleSave() {
    saveMutation.mutate();
  }

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      if (changes.some(c => c.type !== 'select' && c.type !== 'dimensions')) {
        setIsDirty(true);
      }
      // Moving a node by hand ends the auto-layout undo: undoing would throw that move away.
      if (changes.some(c => c.type === 'position' && c.dragging)) {
        setLayoutUndo(null);
      }
      setRfNodes(nds => applyNodeChanges(changes, nds));
    },
    []
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      if (changes.some(c => c.type !== 'select')) {
        setIsDirty(true);
      }
      setRfEdges(eds => applyEdgeChanges(changes, eds));
    },
    []
  );
  const onConnect = useCallback(
    (connection: Connection) => {
      setIsDirty(true);
      setRfEdges(eds => addEdge({ ...connection, type: 'deletable' }, eds));
    },
    []
  );

  const deleteEdge = useCallback((id: string) => {
    setIsDirty(true);
    setRfEdges(eds => eds.filter(e => e.id !== id));
  }, []);

  const handleAutoLayout = useCallback(() => {
    let positions: Positions;
    try {
      positions = autoLayout(
        rfNodes.map(n => ({
          id: n.id,
          type: n.type,
          config: n.data,
          width: n.width ?? DEFAULT_NODE_SIZE.width,
          height: n.height ?? DEFAULT_NODE_SIZE.height,
        })),
        rfEdges.map(e => ({ source: e.source, target: e.target, sourceHandle: e.sourceHandle ?? null })),
      ).positions;
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err));
      return;
    }
    const changed = rfNodes.some(n => {
      const p = positions[n.id];
      return p && (p.x !== n.position.x || p.y !== n.position.y);
    });
    if (!changed) return;
    setLayoutUndo(Object.fromEntries(rfNodes.map(n => [n.id, n.position])));
    setRfNodes(nds => nds.map(n => (positions[n.id] ? { ...n, position: positions[n.id] } : n)));
    setIsDirty(true);
    requestAnimationFrame(() => rfInstance?.fitView({ duration: 300 }));
  }, [rfNodes, rfEdges, rfInstance]);

  function handleUndoLayout() {
    if (!layoutUndo) return;
    const previous = layoutUndo;
    setRfNodes(nds => nds.map(n => (previous[n.id] ? { ...n, position: previous[n.id] } : n)));
    setLayoutUndo(null);
    setIsDirty(true);
    requestAnimationFrame(() => rfInstance?.fitView({ duration: 300 }));
  }

  // Shift+Alt+F formats the flow, like "Format Document" in VS Code. Ignored while typing.
  // The listener is registered once and reads the latest callback through the ref, so it never
  // runs against a stale node list.
  const autoLayoutRef = useRef(handleAutoLayout);
  autoLayoutRef.current = handleAutoLayout;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.shiftKey || !e.altKey || e.ctrlKey || e.metaKey || e.code !== 'KeyF') return;
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      autoLayoutRef.current();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  function handleExport() {
    exportFlow(id!).then(data => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${flow!.name.replace(/\s+/g, '-').toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }

  function addNode(type: string, initialConfig?: Record<string, unknown>) {
    const nodeId = `${type}-${Date.now()}`;
    setIsDirty(true);
    setRfNodes(nds => [...nds, {
      id: nodeId,
      type,
      data: { label: initialConfig?.label ?? nodeId, ...(initialConfig ?? {}) },
      position: { x: 200, y: nds.length * 120 },
    }]);
  }

  function addHttpCheckEval() {
    addNode('eval', {
      label: 'HTTP Check',
      logic: 'and',
      conditions: [
        { expression: '{{http-1.status}}', operator: 'eq', value: '200' },
        { expression: '{{http-1.body}}', operator: 'not_empty', value: '' },
      ],
    });
  }

  const selectedRfNode = rfNodes.find(n => n.id === selectedNodeId);
  const selectedModel: FlowNodeModel | null = selectedRfNode
    ? { id: selectedRfNode.id, type: selectedRfNode.type as FlowNodeModel['type'], config: selectedRfNode.data }
    : null;

  const aliasByNodeId = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const n of definition.nodes) {
      const alias = (n.config?.alias as string | undefined)?.trim();
      if (alias) map[n.id] = alias;
    }
    return map;
  }, [definition.nodes]);

  const stepStatusByNodeId = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const s of executionSteps) map[s.nodeId] = s.status;
    return map;
  }, [executionSteps]);

  const decoratedRfNodes = useMemo<Node[]>(() => {
    if (!executionId) return rfNodes;
    return rfNodes.map(n => {
      const status = stepStatusByNodeId[n.id];
      const cls = status ? NODE_STATUS_CLASS[status] ?? 'ff-node-status-idle' : 'ff-node-status-idle';
      return { ...n, className: cls };
    });
  }, [rfNodes, executionId, stepStatusByNodeId]);

  // Auto-open the first failed step when arriving with an execution in Failed state.
  useEffect(() => {
    if (!executionId || autoOpened || !execution || executionSteps.length === 0) return;
    if (execution.status !== 'Failed') { setAutoOpened(true); return; }
    const failed = executionSteps.find(s => s.status === 'Failed');
    if (failed) {
      setSelectedNodeId(failed.nodeId);
      setPanelInitialTab(failed.output ? 'output' : 'config');
    }
    setAutoOpened(true);
  }, [executionId, execution, executionSteps, autoOpened]);

  function updateExecParam(next: string | null, key: 'execution' | 'compare' = 'execution') {
    const params = new URLSearchParams(searchParams);
    if (next) params.set(key, next); else params.delete(key);
    if (key === 'execution' && !next) params.delete('compare');
    setSearchParams(params, { replace: true });
    if (key === 'execution') setAutoOpened(false);
  }

  if (isLoading) return <p className="p-6 text-muted-foreground">Loading…</p>;
  if (!flow) return <p className="p-6 text-destructive">Flow not found.</p>;

  const neverPublished = flow.publishedRevisionNumber === null;
  const draftDiffersFromLive = flow.hasUnpublishedChanges;

  const toolbarButton = 'h-8 gap-1.5 px-2 text-[13px] text-muted-foreground hover:text-foreground';

  return (
    <div className="h-full flex flex-col">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-4">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => navigate('/flows')}
          aria-label="Back to flows"
          title="Back to flows"
        >
          <ArrowLeft />
        </Button>
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-[15px] font-semibold">{flow.name}</h1>
          {neverPublished ? (
            <Badge variant="secondary">Never published</Badge>
          ) : draftDiffersFromLive ? (
            <Badge variant="warning">Draft ahead of live</Badge>
          ) : (
            <Badge variant="success" className="shrink-0 whitespace-nowrap">Live · #{flow.publishedRevisionNumber}</Badge>
          )}
          {flow.scheduleEnabled && flow.schedule && (
            <span
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground"
              title={flow.nextRunAt ? `Next run: ${new Date(flow.nextRunAt).toLocaleString()}` : ''}
            >
              <Clock className="h-3 w-3" />
              {flow.schedule}
            </span>
          )}
        </div>

        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" className={toolbarButton} onClick={() => setHistoryOpen(true)} aria-label="History" title="Published versions">
            <History />
            <span className="hidden 2xl:inline">History</span>
          </Button>
          <Button variant="ghost" className={toolbarButton} onClick={() => setScheduleOpen(true)} aria-label="Schedule" title="Schedule">
            <CalendarClock />
            <span className="hidden 2xl:inline">Schedule</span>
          </Button>
          <Button
            variant="ghost"
            className={toolbarButton}
            onClick={() => setWebhookSecurityOpen(true)}
            aria-label="Webhook"
            title="Public or token-protected webhook"
          >
            {flow.webhookAuth === 'Token' ? <Lock /> : <Webhook />}
            <span className="hidden 2xl:inline">Webhook</span>
          </Button>
          <Button
            variant="ghost"
            className={toolbarButton}
            onClick={() => setSettingsOpen(true)}
            aria-label="Settings"
            title="Name, description, tags, label template"
          >
            <Settings2 />
            <span className="hidden 2xl:inline">Settings</span>
            {(flow.tags.length > 0 || flow.description || flow.labelTemplate) && (
              <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
            )}
          </Button>
          <Button variant="ghost" className={toolbarButton} onClick={() => openReference()} aria-label="Reference" title="Template reference">
            <BookOpen />
            <span className="hidden 2xl:inline">Reference</span>
          </Button>
          <Button variant="ghost" className={toolbarButton} onClick={handleExport} aria-label="Export" title="Export">
            <Download />
            <span className="hidden 2xl:inline">Export</span>
          </Button>

          <div className="mx-2 h-5 w-px bg-border" />

          {isDirty && (
            <span className="mr-1 inline-flex items-center gap-1.5 text-xs text-warning" title="Unsaved changes">
              <span className="h-1.5 w-1.5 rounded-full bg-warning" />
              Unsaved
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleSave}
            disabled={saveMutation.isPending}
            title="Saves the draft. Does not change what is running."
          >
            {saveMutation.isPending ? 'Saving…' : 'Save draft'}
          </Button>
          <Button
            size="sm"
            onClick={() => setPublishOpen(true)}
            disabled={isDirty || !draftDiffersFromLive}
            title={
              isDirty
                ? 'Save the draft before publishing it.'
                : draftDiffersFromLive
                  ? 'Makes the draft the version that runs in production.'
                  : 'The draft is already live.'
            }
          >
            Publish
          </Button>
        </div>
      </header>

      {neverPublished ? (
        <div className="shrink-0 border-b border-warning/30 bg-warning/10 px-4 py-1.5 text-[13px] text-foreground/80">
          This flow has never been published — nothing of it runs yet. Use a test run to try the
          draft, then publish it to go live.
        </div>
      ) : draftDiffersFromLive ? (
        <div className="shrink-0 border-b border-warning/30 bg-warning/10 px-4 py-1.5 text-[13px] text-foreground/80">
          The draft differs from what is running. Live is revision #{flow.publishedRevisionNumber}
          {flow.publishedAt && `, published ${new Date(flow.publishedAt).toLocaleString()}`}
          {flow.publishedByEmail && ` by ${flow.publishedByEmail}`}. Your changes take effect only
          when you publish.
        </div>
      ) : (
        <div className="shrink-0 border-b border-border bg-muted/40 px-4 py-1.5 text-[13px] text-muted-foreground">
          Draft matches live (revision #{flow.publishedRevisionNumber}
          {flow.publishedAt && `, published ${new Date(flow.publishedAt).toLocaleString()}`}).
          {flow.executionsSincePublish > 0 && (
            <> {flow.executionsSincePublish} executions since, {flow.failedSincePublish} failed.</>
          )}
        </div>
      )}

      <EdgeActionsContext.Provider value={{ deleteEdge }}>
      <ExecutionViewProvider
        primary={execution && executionSteps ? { execution, steps: executionSteps } : null}
        compare={compareExecution && compareSteps ? { execution: compareExecution, steps: compareSteps } : null}
      >
      <div className="flex-1 relative bg-background">
        <div className="absolute left-3 top-3 z-10 flex items-center gap-2">
          <StepPicker
            onAddNode={addNode}
            onAddPreset={presetId => { if (presetId === 'http-check') addHttpCheckEval(); }}
          />
          <ExecutionPicker
            flowId={id!}
            selectedId={executionId}
            compareId={compareId}
            onSelect={execId => updateExecParam(execId, 'execution')}
            onSelectCompare={execId => updateExecParam(execId, 'compare')}
          />
        </div>
        <ReactFlow
          nodes={decoratedRfNodes}
          edges={rfEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={(_, node) => { setSelectedNodeId(node.id); setPanelInitialTab(undefined); }}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onInit={setRfInstance}
          fitView
          fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
          snapToGrid
          snapGrid={[8, 8]}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1.2} />
          <Controls>
            <ControlButton
              onClick={handleAutoLayout}
              title="Auto-layout top-down (Shift+Alt+F)"
              aria-label="Auto-layout"
            >
              <Network />
            </ControlButton>
          </Controls>
          <MiniMap pannable zoomable nodeBorderRadius={6} />
        </ReactFlow>

        {layoutUndo && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 rounded-lg border border-border bg-popover px-3 py-1.5 text-xs shadow-lg">
            <span className="text-muted-foreground">Layout applied</span>
            <button
              onClick={handleUndoLayout}
              className="font-medium text-primary hover:underline"
            >
              Undo layout
            </button>
          </div>
        )}

        {selectedModel && (
          <NodeConfigPanel
            node={selectedModel}
            aliasByNodeId={aliasByNodeId}
            initialTab={panelInitialTab}
            onUpdate={updated => {
              setIsDirty(true);
              setRfNodes(nds => nds.map(n =>
                n.id === updated.id
                  ? { ...n, data: { label: n.data.label, ...updated.config } }
                  : n
              ));
            }}
            onClose={() => setSelectedNodeId(null)}
            onOpenReference={openReference}
            onDelete={() => {
              setIsDirty(true);
              setRfNodes(nds => nds.filter(n => n.id !== selectedModel.id));
              setRfEdges(eds => eds.filter(e => e.source !== selectedModel.id && e.target !== selectedModel.id));
              setSelectedNodeId(null);
            }}
          />
        )}
      </div>
      </ExecutionViewProvider>
      </EdgeActionsContext.Provider>

      {webhookSecurityOpen && (
        <WebhookSecurityModal flow={flow} onClose={() => setWebhookSecurityOpen(false)} />
      )}
      {scheduleOpen && (
        <ScheduleModal flow={flow} onClose={() => setScheduleOpen(false)} />
      )}

      {settingsOpen && (
        <FlowSettingsModal flow={flow} onClose={() => setSettingsOpen(false)} />
      )}

      <TemplateReferenceDrawer
        open={referenceOpen}
        onClose={() => setReferenceOpen(false)}
        initialTab="reference"
        initialSection={referenceSection}
      />

      {publishOpen && (
        <PublishModal
          flowName={flow.name}
          isPending={publishMutation.isPending}
          error={publishMutation.error ? 'Publishing failed. Check the definition and try again.' : null}
          onCancel={() => setPublishOpen(false)}
          onConfirm={name => publishMutation.mutate(name)}
        />
      )}

      {historyOpen && (
        <RevisionsDrawer
          flowId={id!}
          onClose={() => setHistoryOpen(false)}
          onRestored={() => {
            setInitialized(false);
            qc.invalidateQueries({ queryKey: ['flows', id] });
            qc.invalidateQueries({ queryKey: ['flows', id, 'revisions'] });
            setHistoryOpen(false);
          }}
        />
      )}
    </div>
  );
}

function PublishModal({
  flowName, isPending, error, onCancel, onConfirm,
}: {
  flowName: string;
  isPending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (revisionName: string) => void;
}) {
  const [name, setName] = useState('');
  const trimmed = name.trim();

  return (
    <Dialog open onOpenChange={open => { if (!open) onCancel(); }}>
      <DialogContent aria-label="Publish flow" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Publish “{flowName}”</DialogTitle>
          <DialogDescription>
            This makes the current draft the version that runs in production, and records it as a
            revision you can roll back to.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="revision-name">What changed?</Label>
          <Input
            id="revision-name"
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && trimmed) onConfirm(trimmed); }}
            placeholder="e.g. Retry the Asana call on 429"
          />
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onConfirm(trimmed)} disabled={!trimmed || isPending}>
            {isPending ? 'Publishing…' : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevisionsDrawer({
  flowId, onClose, onRestored,
}: {
  flowId: string;
  onClose: () => void;
  onRestored: () => void;
}) {
  const qc = useQueryClient();
  const { data: revisions, isLoading } = useQuery({
    queryKey: ['flows', flowId, 'revisions'],
    queryFn: () => listRevisions(flowId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['flows', flowId, 'revisions'] });
    onRestored();
  };

  const restoreMutation = useMutation({
    mutationFn: (revisionId: string) => restoreRevision(flowId, revisionId),
    onSuccess: invalidate,
  });

  // Publishing an earlier revision is the rollback: same endpoint, same append-only history.
  const rollbackMutation = useMutation({
    mutationFn: (rev: { id: string; revisionNumber: number }) =>
      publishFlow(flowId, {
        revisionName: `Rollback to #${rev.revisionNumber}`,
        sourceRevisionId: rev.id,
      }),
    onSuccess: invalidate,
  });

  const busy = restoreMutation.isPending || rollbackMutation.isPending;

  return (
    <Sheet open onOpenChange={open => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-96 overflow-y-auto p-5 sm:max-w-md">
        <SheetHeader className="mb-4">
          <SheetTitle>Published versions</SheetTitle>
          <SheetDescription>Every publish is recorded here (last 20, plus whatever is live).</SheetDescription>
        </SheetHeader>
        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {!isLoading && (revisions?.length ?? 0) === 0 && (
          <p className="text-sm text-muted-foreground">Never published.</p>
        )}
        <ul className="space-y-2">
          {revisions?.map(r => (
            <li
              key={r.id}
              className={`rounded-lg border bg-card p-3 text-sm ${
                r.isCurrentlyPublished ? 'border-primary/60 ring-1 ring-primary/20' : 'border-border'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-muted-foreground text-xs">#{r.revisionNumber}</span>
                <span className="font-medium truncate flex-1" title={r.name}>{r.name}</span>
                {r.isCurrentlyPublished && (
                  <Badge variant="success">Live</Badge>
                )}
              </div>
              <div className="text-muted-foreground text-xs mt-1">
                {new Date(r.createdAt).toLocaleString()}
              </div>
              {r.createdByEmail && (
                <div className="text-muted-foreground text-xs">{r.createdByEmail}</div>
              )}
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => restoreMutation.mutate(r.id)}
                  disabled={busy}
                  title="Loads this version into the draft. Does not change what is running."
                >
                  Load into draft
                </Button>
                {!r.isCurrentlyPublished && (
                  <Button
                    size="xs"
                    onClick={() => {
                      if (window.confirm(
                        `Make revision #${r.revisionNumber} live? It takes effect for new ` +
                        `executions immediately (webhooks within ~30s). Your draft is left alone.`
                      )) {
                        rollbackMutation.mutate(r);
                      }
                    }}
                    disabled={busy}
                  >
                    Make live
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  );
}
